/**
 * Type definitions for Handlungsempfehlungen (Action Recommendations) data
 */

import { FUNNEL_CATEGORIES } from './massnahmen';
import type { FunnelCategory } from './massnahmen';

/**
 * Single Umsetzungsplan (implementation plan) step
 */
export interface UmsetzungsplanSchritt {
  Name: string;
  Beschreibung: string;
  Schritt: number;
}

/**
 * Single Handlungsempfehlung (action recommendation) table entry
 */
export interface HandlungsempfehlungTableEntry {
  Name: string;
  Beschreibung: string;
  Effekt_nach_Monaten: number;
  Priorität: number;
  Zielgruppe: string;
  CTA_Text: string;
  Umsetzungsplan: UmsetzungsplanSchritt[];
}

/**
 * Main Handlungsempfehlungen data structure from API
 */
export interface HandlungsempfehlungenData {
  Handlungsempfehlungen_tabelle: Record<FunnelCategory, HandlungsempfehlungTableEntry[]>;
}

/**
 * Combined data structure for card and overlay
 */
export interface HandlungsempfehlungenFullData {
  main: HandlungsempfehlungenData;
}

/**
 * Flatten categorized Handlungsempfehlungen_tabelle into a flat array (for card backward compat)
 */
export function flattenHandlungsempfehlungenTabelle(
  table: Record<FunnelCategory, HandlungsempfehlungTableEntry[]> | HandlungsempfehlungTableEntry[] | undefined
): HandlungsempfehlungTableEntry[] {
  if (!table) return [];
  // Handle legacy flat array format
  if (Array.isArray(table)) return table;
  return FUNNEL_CATEGORIES.flatMap(cat => table[cat] || []);
}

/**
 * Get priority color based on value (1 = highest priority)
 */
export function getPriorityColor(priority: number): string {
  if (priority === 1) return '#22c55e'; // green-500 - highest priority
  if (priority === 2) return '#84cc16'; // lime-500
  if (priority === 3) return '#eab308'; // yellow-500
  if (priority === 4) return '#f97316'; // orange-500
  return '#ef4444'; // red-500 - lowest priority
}

/**
 * Get priority badge style based on value
 */
export function getPriorityBadgeStyle(priority: number): { background: string; text: string } {
  if (priority === 1) return { background: 'bg-green-100', text: 'text-green-700' };
  if (priority === 2) return { background: 'bg-lime-100', text: 'text-lime-700' };
  if (priority === 3) return { background: 'bg-yellow-100', text: 'text-yellow-700' };
  if (priority === 4) return { background: 'bg-orange-100', text: 'text-orange-700' };
  return { background: 'bg-red-100', text: 'text-red-700' };
}

/**
 * Get effect timeframe color based on months
 */
export function getEffectColor(months: number): string {
  if (months <= 1) return '#22c55e'; // green - quick wins
  if (months <= 3) return '#84cc16'; // lime
  if (months <= 6) return '#eab308'; // yellow
  if (months <= 12) return '#f97316'; // orange
  return '#ef4444'; // red - long-term
}

/**
 * Parse raw JSON string from API into structured Handlungsempfehlungen data
 */
export function parseHandlungsempfehlungenData(jsonString: string): HandlungsempfehlungenFullData | null {
  try {
    let data = jsonString;

    // If the string is wrapped in quotes, try to parse it first
    if (typeof data === 'string' && (data.startsWith('"') || data.startsWith("'"))) {
      try {
        data = JSON.parse(data);
      } catch {
        // If it fails, continue with original string
      }
    }

    const parsed = typeof data === 'string' ? JSON.parse(data) : data;

    // Handle case where it's an array
    const mainData = Array.isArray(parsed) ? parsed[0] : parsed;

    if (!mainData) {
      return null;
    }

    return {
      main: mainData as HandlungsempfehlungenData,
    };
  } catch {
    return null;
  }
}

const makeSteps = (steps: [string, string][]): UmsetzungsplanSchritt[] =>
  steps.map(([Name, Beschreibung], i) => ({ Name, Beschreibung, Schritt: i + 1 }));

/**
 * Generate mock data for development
 */
export function getMockHandlungsempfehlungenData(): HandlungsempfehlungenFullData {
  const mockData: HandlungsempfehlungenData = {
    Handlungsempfehlungen_tabelle: {
      Awareness: [
        {
          Name: 'Social Media Präsenz aufbauen',
          Beschreibung: 'Aufbau einer professionellen Präsenz auf LinkedIn und Instagram mit regelmäßigen Beiträgen zu Branchenthemen.',
          Effekt_nach_Monaten: 3,
          Priorität: 1,
          Zielgruppe: 'Entscheider in KMUs',
          CTA_Text: 'Social Media Strategie starten',
          Umsetzungsplan: makeSteps([
            ['Accounts einrichten', 'Professionelle Business-Profile auf LinkedIn und Instagram erstellen mit vollständigen Unternehmensinformationen.'],
            ['Content-Plan erstellen', 'Redaktionsplan für die ersten 3 Monate mit Themen, Formaten und Veröffentlichungsterminen.'],
            ['Erste Inhalte produzieren', 'Erstellung von 10-15 Beiträgen im Voraus, inkl. Grafiken und Texte.'],
            ['Community aufbauen', 'Aktives Engagement durch Kommentieren, Teilen und Vernetzen mit relevanten Kontakten.'],
            ['Performance analysieren', 'Monatliche Auswertung der KPIs und Anpassung der Strategie basierend auf den Ergebnissen.'],
          ]),
        },
        {
          Name: 'Branchenevents besuchen',
          Beschreibung: 'Präsenz auf den wichtigsten Branchenveranstaltungen und Messen zur Steigerung der Markenbekanntheit.',
          Effekt_nach_Monaten: 4,
          Priorität: 2,
          Zielgruppe: 'Branchenakteure & Entscheider',
          CTA_Text: 'Eventkalender erstellen',
          Umsetzungsplan: makeSteps([
            ['Relevante Events identifizieren', 'Recherche und Liste der Top-10-Branchenevents im kommenden Jahr.'],
            ['Budget planen', 'Kostenaufstellung für Standgebühren, Material und Personalkosten.'],
            ['Messestand konzipieren', 'Design und Produktion eines professionellen Messestands.'],
            ['Networking-Strategie', 'Vorbereitung von Gesprächsleitfäden und Kontaktlisten.'],
            ['Follow-up Prozess', 'Systematische Nachbearbeitung aller geknüpften Kontakte.'],
          ]),
        },
        {
          Name: 'Thought-Leadership aufbauen',
          Beschreibung: 'Positionierung als Branchenexperte durch Fachartikel, Gastbeiträge und Vorträge.',
          Effekt_nach_Monaten: 6,
          Priorität: 3,
          Zielgruppe: 'Fachpublikum & Medien',
          CTA_Text: 'Expertenstrategie starten',
          Umsetzungsplan: makeSteps([
            ['Themenkompetenz definieren', 'Festlegung der Kernthemen für die Expertenpositionierung.'],
            ['Fachartikel verfassen', 'Erstellung von 2 Fachartikeln pro Monat für relevante Medien.'],
            ['Vortragsanfragen annehmen', 'Aktive Bewerbung als Speaker auf Branchenkonferenzen.'],
            ['Medienpartnerschaften', 'Aufbau von Beziehungen zu relevanten Fachmedien.'],
            ['Wirkung messen', 'Tracking von Reichweite, Zitierungen und eingehenden Anfragen.'],
          ]),
        },
        {
          Name: 'PR-Kampagne starten',
          Beschreibung: 'Gezielte Pressearbeit und Medienkooperationen für erhöhte Sichtbarkeit in der Zielgruppe.',
          Effekt_nach_Monaten: 2,
          Priorität: 2,
          Zielgruppe: 'Fachpresse & Branchenmedien',
          CTA_Text: 'PR-Strategie entwickeln',
          Umsetzungsplan: makeSteps([
            ['Medienliste erstellen', 'Zusammenstellung relevanter Fachmedien und Journalistenkontakte.'],
            ['Pressemitteilung verfassen', 'Erstellung einer überzeugenden Unternehmensstory.'],
            ['Presseverteiler aufbauen', 'Einrichtung eines professionellen Presseverteilers.'],
            ['Medienkontakte pflegen', 'Regelmäßige Hintergrundgespräche mit Journalisten.'],
            ['Clipping & Analyse', 'Monitoring und Auswertung der Medienberichterstattung.'],
          ]),
        },
        {
          Name: 'Podcast starten',
          Beschreibung: 'Eigener Branchen-Podcast mit wöchentlichen Folgen zu relevanten Themen und Experteninterviews.',
          Effekt_nach_Monaten: 5,
          Priorität: 3,
          Zielgruppe: 'Brancheninteressierte',
          CTA_Text: 'Podcast-Konzept erstellen',
          Umsetzungsplan: makeSteps([
            ['Konzept & Format', 'Festlegung des Podcast-Formats, der Themen und der Zielgruppe.'],
            ['Technik einrichten', 'Anschaffung von Equipment und Einrichtung des Aufnahmesetups.'],
            ['Erste Folgen produzieren', 'Aufnahme und Schnitt der ersten 5 Pilotfolgen.'],
            ['Veröffentlichung & Distribution', 'Upload auf alle relevanten Podcast-Plattformen.'],
            ['Promotion & Wachstum', 'Cross-Promotion über Social Media und Newsletter.'],
          ]),
        },
      ],
      Consideration: [
        {
          Name: 'E-Mail Newsletter einführen',
          Beschreibung: 'Regelmäßiger Newsletter mit Branchennews, Tipps und exklusiven Angeboten für Bestandskunden und Interessenten.',
          Effekt_nach_Monaten: 2,
          Priorität: 1,
          Zielgruppe: 'Bestandskunden & Leads',
          CTA_Text: 'Newsletter System einrichten',
          Umsetzungsplan: makeSteps([
            ['Newsletter-Tool auswählen', 'Vergleich und Auswahl eines geeigneten E-Mail-Marketing-Tools (z.B. Mailchimp, CleverReach).'],
            ['Anmeldeformular integrieren', 'Einbindung eines Opt-in Formulars auf der Website mit DSGVO-konformer Einwilligung.'],
            ['Template gestalten', 'Erstellung eines responsive E-Mail-Templates im Corporate Design.'],
            ['Content-Strategie definieren', 'Festlegung von Rubriken, Versandfrequenz und Inhaltstypen.'],
            ['Automatisierung einrichten', 'Setup von Welcome-Mails und automatisierten Sequenzen für neue Abonnenten.'],
          ]),
        },
        {
          Name: 'Webinar-Reihe starten',
          Beschreibung: 'Monatliche Online-Seminare zu relevanten Branchenthemen zur Lead-Generierung.',
          Effekt_nach_Monaten: 4,
          Priorität: 2,
          Zielgruppe: 'Interessierte Neukunden',
          CTA_Text: 'Erstes Webinar planen',
          Umsetzungsplan: makeSteps([
            ['Webinar-Plattform wählen', 'Auswahl einer geeigneten Plattform (Zoom, Teams, GoToWebinar) basierend auf Anforderungen.'],
            ['Themenplan erstellen', 'Definition von 6 Webinar-Themen für das erste Halbjahr mit Mehrwert für die Zielgruppe.'],
            ['Landing Page einrichten', 'Erstellung einer Anmeldeseite mit überzeugenden Inhalten und einfacher Registrierung.'],
            ['Promotion-Strategie entwickeln', 'Plan zur Bewerbung über Social Media, Newsletter und Partner.'],
            ['Pilot-Webinar durchführen', 'Durchführung des ersten Webinars mit anschließender Auswertung und Optimierung.'],
          ]),
        },
        {
          Name: 'Case Studies veröffentlichen',
          Beschreibung: 'Detaillierte Erfolgsgeschichten bestehender Kunden als Referenz und Vertrauensbeweis.',
          Effekt_nach_Monaten: 3,
          Priorität: 1,
          Zielgruppe: 'Potenzielle Neukunden',
          CTA_Text: 'Erste Case Study erstellen',
          Umsetzungsplan: makeSteps([
            ['Referenzkunden auswählen', 'Identifikation von 5 Kunden mit den überzeugendsten Erfolgsgeschichten.'],
            ['Interviews durchführen', 'Strukturierte Gespräche mit Kunden über Herausforderungen und Ergebnisse.'],
            ['Case Study verfassen', 'Professionelle Aufbereitung mit Zahlen, Zitaten und Grafiken.'],
            ['Design & Layout', 'Gestaltung als PDF-Download und Webseite im Corporate Design.'],
            ['Verbreitung starten', 'Integration in Sales-Prozess, Website und Social Media.'],
          ]),
        },
        {
          Name: 'Kostenloser Ratgeber anbieten',
          Beschreibung: 'E-Book oder Whitepaper als Lead-Magnet für qualifizierte Kontaktdaten.',
          Effekt_nach_Monaten: 2,
          Priorität: 2,
          Zielgruppe: 'Informationssuchende Interessenten',
          CTA_Text: 'Ratgeber erstellen',
          Umsetzungsplan: makeSteps([
            ['Thema recherchieren', 'Identifikation des drängendsten Problems der Zielgruppe.'],
            ['Inhalt erstellen', 'Verfassen eines 20-30-seitigen Ratgebers mit praktischen Tipps.'],
            ['Professionell gestalten', 'Layouterstellung im Corporate Design mit Grafiken.'],
            ['Download-Funnel einrichten', 'Landing Page mit Formular für den Ratgeber-Download.'],
            ['Promotion starten', 'Bewerbung über Social Media, Ads und bestehende Kontakte.'],
          ]),
        },
        {
          Name: 'Vergleichsrechner entwickeln',
          Beschreibung: 'Interaktives Online-Tool, das den Nutzen Ihrer Lösung berechnet und darstellt.',
          Effekt_nach_Monaten: 3,
          Priorität: 3,
          Zielgruppe: 'Preisbewusste Interessenten',
          CTA_Text: 'Tool-Konzept erstellen',
          Umsetzungsplan: makeSteps([
            ['Berechnungslogik definieren', 'Festlegung der Eingabeparameter und ROI-Berechnung.'],
            ['UI/UX Design', 'Gestaltung einer intuitiven Benutzeroberfläche.'],
            ['Entwicklung', 'Technische Umsetzung als Web-Applikation.'],
            ['Testing & Optimierung', 'Nutzertests und Verbesserung der Benutzerführung.'],
            ['Integration & Launch', 'Einbindung auf der Website und Bewerbung.'],
          ]),
        },
      ],
      Conversion: [
        {
          Name: 'Google Bewertungen optimieren',
          Beschreibung: 'Systematische Steigerung der Google-Bewertungen durch aktive Kundenansprache.',
          Effekt_nach_Monaten: 1,
          Priorität: 1,
          Zielgruppe: 'Zufriedene Bestandskunden',
          CTA_Text: 'Bewertungskampagne starten',
          Umsetzungsplan: makeSteps([
            ['Google Business optimieren', 'Vollständige Aktualisierung des Google Business Profils mit allen relevanten Informationen.'],
            ['Bewertungsprozess definieren', 'Festlegung wann und wie Kunden um Bewertungen gebeten werden.'],
            ['E-Mail-Vorlage erstellen', 'Persönliche und freundliche Vorlage für Bewertungsanfragen.'],
            ['Erste Kampagne starten', 'Ansprache der zufriedensten Bestandskunden mit persönlicher Bitte um Bewertung.'],
            ['Feedback-Management einführen', 'Regelmäßige Antworten auf alle Bewertungen und Umgang mit negativem Feedback.'],
          ]),
        },
        {
          Name: 'Landing Pages A/B-testen',
          Beschreibung: 'Systematische Conversion-Optimierung durch A/B-Tests der wichtigsten Seiten.',
          Effekt_nach_Monaten: 2,
          Priorität: 1,
          Zielgruppe: 'Website-Besucher',
          CTA_Text: 'A/B-Test starten',
          Umsetzungsplan: makeSteps([
            ['Testing-Tool einrichten', 'Setup von Google Optimize oder Optimizely für A/B-Tests.'],
            ['Hypothesen formulieren', 'Identifikation der größten Conversion-Hindernisse.'],
            ['Varianten erstellen', 'Design alternativer Seitenvarianten basierend auf Hypothesen.'],
            ['Tests durchführen', 'Start der A/B-Tests mit ausreichender Stichprobengröße.'],
            ['Ergebnisse implementieren', 'Umsetzung der Gewinnerversionen auf allen Seiten.'],
          ]),
        },
        {
          Name: 'Sales-Funnel automatisieren',
          Beschreibung: 'Automatisierte Lead-Qualifizierung und Nurturing durch Marketing-Automation.',
          Effekt_nach_Monaten: 3,
          Priorität: 2,
          Zielgruppe: 'Marketing-qualifizierte Leads',
          CTA_Text: 'Automation einrichten',
          Umsetzungsplan: makeSteps([
            ['Lead-Scoring definieren', 'Festlegung der Kriterien für Lead-Qualifizierung.'],
            ['Automation-Tool wählen', 'Auswahl und Setup des Marketing-Automation-Tools.'],
            ['Workflows erstellen', 'Erstellung automatisierter E-Mail-Sequenzen und Trigger.'],
            ['CRM-Integration', 'Anbindung an das CRM-System für nahtlose Übergabe.'],
            ['Monitoring & Optimierung', 'Regelmäßige Analyse und Verbesserung der Conversion-Raten.'],
          ]),
        },
        {
          Name: 'Chatbot implementieren',
          Beschreibung: 'KI-gestützter Chatbot auf der Website zur Besucherberatung und Lead-Erfassung.',
          Effekt_nach_Monaten: 1,
          Priorität: 2,
          Zielgruppe: 'Website-Besucher',
          CTA_Text: 'Chatbot einrichten',
          Umsetzungsplan: makeSteps([
            ['Gesprächsszenarien definieren', 'Mapping der häufigsten Besucherfragen und Antworten.'],
            ['Chatbot-Plattform wählen', 'Auswahl einer geeigneten Chatbot-Lösung.'],
            ['Dialoge konfigurieren', 'Einrichtung der Gesprächsflüsse und Lead-Erfassung.'],
            ['Integration testen', 'Technische Einbindung und Testphase auf der Website.'],
            ['Optimierung starten', 'Analyse der Chatbot-Gespräche und kontinuierliche Verbesserung.'],
          ]),
        },
        {
          Name: 'Testimonials sammeln',
          Beschreibung: 'Systematische Sammlung und Aufbereitung von Kundenstimmen für die Website.',
          Effekt_nach_Monaten: 2,
          Priorität: 3,
          Zielgruppe: 'Zufriedene Kunden',
          CTA_Text: 'Testimonial-Kampagne starten',
          Umsetzungsplan: makeSteps([
            ['Kunden identifizieren', 'Auswahl der zufriedensten Kunden für Testimonials.'],
            ['Anfrage versenden', 'Persönliche Bitte um Feedback und Erfahrungsbericht.'],
            ['Video-Testimonials aufnehmen', 'Professionelle Aufnahme von Video-Statements.'],
            ['Aufbereitung & Design', 'Gestaltung der Testimonials für verschiedene Kanäle.'],
            ['Website-Integration', 'Einbindung auf relevanten Seiten für maximale Wirkung.'],
          ]),
        },
      ],
      Loyalty: [
        {
          Name: 'Lokale Partnerschaften aufbauen',
          Beschreibung: 'Kooperationen mit komplementären Unternehmen in der Region zur gegenseitigen Empfehlung.',
          Effekt_nach_Monaten: 6,
          Priorität: 2,
          Zielgruppe: 'Regionale Unternehmen',
          CTA_Text: 'Partnersuche beginnen',
          Umsetzungsplan: makeSteps([
            ['Potenzielle Partner identifizieren', 'Liste von 20-30 komplementären Unternehmen in der Region erstellen.'],
            ['Kontaktaufnahme vorbereiten', 'Erstellung einer Präsentation mit Partnerschaftsvorteilen und Kooperationsmodellen.'],
            ['Erstgespräche führen', 'Persönliche Vorstellung bei potenziellen Partnern und Interessensabklärung.'],
            ['Partnervereinbarungen schließen', 'Ausarbeitung und Unterzeichnung von Kooperationsverträgen.'],
            ['Gemeinsame Aktionen starten', 'Launch der ersten gemeinsamen Marketing-Aktivitäten und Events.'],
          ]),
        },
        {
          Name: 'Kundenbindungsprogramm einführen',
          Beschreibung: 'Treueprogramm mit Rabatten, Exklusivzugang und VIP-Vorteilen für loyale Bestandskunden.',
          Effekt_nach_Monaten: 3,
          Priorität: 1,
          Zielgruppe: 'Langjährige Bestandskunden',
          CTA_Text: 'Treueprogramm starten',
          Umsetzungsplan: makeSteps([
            ['Programmstruktur definieren', 'Festlegung der Stufen, Vorteile und Prämien des Treueprogramms.'],
            ['Technische Umsetzung', 'Integration in CRM und Kundenkonto-Bereich.'],
            ['Kommunikation erstellen', 'Entwicklung der Willkommens- und Informationsmaterialien.'],
            ['Pilotphase starten', 'Einführung bei den Top-50-Kunden mit Feedback-Schleife.'],
            ['Rollout & Skalierung', 'Ausweitung auf alle Bestandskunden mit regelmäßiger Optimierung.'],
          ]),
        },
        {
          Name: 'Kundenumfragen durchführen',
          Beschreibung: 'Regelmäßige Zufriedenheitsbefragungen zur proaktiven Problemlösung und Verbesserung.',
          Effekt_nach_Monaten: 1,
          Priorität: 1,
          Zielgruppe: 'Alle Bestandskunden',
          CTA_Text: 'Erste Umfrage starten',
          Umsetzungsplan: makeSteps([
            ['Umfrage-Tool wählen', 'Auswahl eines geeigneten Tools (z.B. Typeform, SurveyMonkey).'],
            ['Fragebogen erstellen', 'Entwicklung eines kurzen, aussagekräftigen Fragebogens.'],
            ['Erste Umfrage versenden', 'Versand an alle Bestandskunden mit persönlicher Ansprache.'],
            ['Ergebnisse analysieren', 'Auswertung der Daten und Identifikation von Handlungsfeldern.'],
            ['Maßnahmen umsetzen', 'Konkrete Verbesserungen basierend auf dem Kundenfeedback.'],
          ]),
        },
        {
          Name: 'Empfehlungsprogramm starten',
          Beschreibung: 'Prämienbasiertes Programm, das zufriedene Kunden für Weiterempfehlungen belohnt.',
          Effekt_nach_Monaten: 2,
          Priorität: 2,
          Zielgruppe: 'Zufriedene Bestandskunden',
          CTA_Text: 'Empfehlungsprogramm einrichten',
          Umsetzungsplan: makeSteps([
            ['Prämienmodell entwickeln', 'Festlegung attraktiver Prämien für erfolgreiche Empfehlungen.'],
            ['Tracking-System einrichten', 'Technisches Setup zur Nachverfolgung von Empfehlungen.'],
            ['Werbematerialien erstellen', 'Vorlagen und Tools für einfaches Teilen und Empfehlen.'],
            ['Kunden informieren', 'Vorstellung des Programms bei allen Bestandskunden.'],
            ['Ergebnisse messen', 'Monatliche Auswertung der Empfehlungsquoten und ROI.'],
          ]),
        },
        {
          Name: 'Exklusive Kunden-Events',
          Beschreibung: 'Regelmäßige Events und Workshops nur für Bestandskunden zur Stärkung der Bindung.',
          Effekt_nach_Monaten: 4,
          Priorität: 3,
          Zielgruppe: 'Premium-Bestandskunden',
          CTA_Text: 'Erstes Event planen',
          Umsetzungsplan: makeSteps([
            ['Event-Konzept entwickeln', 'Planung von Formaten wie Workshops, Networking-Dinner, Masterclasses.'],
            ['Jahreskalender erstellen', 'Festlegung von 4-6 Events pro Jahr mit Themen und Terminen.'],
            ['Einladungsmanagement', 'Aufbau eines VIP-Einladungsprozesses für Top-Kunden.'],
            ['Event durchführen', 'Professionelle Durchführung des ersten Pilot-Events.'],
            ['Feedback & Optimierung', 'Nachbefragung und kontinuierliche Verbesserung des Formats.'],
          ]),
        },
      ],
    },
  };

  return {
    main: mockData,
  };
}
